import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { authAdapter } from './adapter';
import type { CurrentUser } from './api';
import { fetchMe } from './authClient';
import { returnToFor } from './returnTo';
import { type AuthState, resolveSession } from './session';
import { endViewAs } from './viewAs';
import { separateViewAsStorage } from './viewAsStorage';

interface AuthContextValue {
  state: AuthState;
  /** Starts Google sign-in and brings the visitor back to the page they are on. */
  signIn(): Promise<void>;
  signOut(): Promise<void>;
  /** Asks the server again after an error. */
  retry(): void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;

    void resolveSession({
      getToken: () => authAdapter.getToken(),
      fetchMe,
      signOut: () => authAdapter.signOut(),
    }).then((resolved) => {
      if (!cancelled) {
        if (resolved.status === 'authorized') separateViewAsStorage(resolved.user);
        setState(resolved);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const signIn = useCallback(
    () => authAdapter.signInWithGoogle(returnToFor(window.location)),
    [],
  );

  const signOut = useCallback(async (): Promise<void> => {
    setState({ status: 'signed-out' });
    // Signing out ends any Log in as user session first, while the token that proves who you are still works.
    await endViewAs('sign_out');
    try {
      await authAdapter.signOut();
    } catch {
      // The browser still treats the visitor as signed out; the next load asks the server again.
    }
  }, []);

  const retry = useCallback(() => {
    setState({ status: 'loading' });
    setAttempt((current) => current + 1);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ state, signIn, signOut, retry }),
    [state, signIn, signOut, retry],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used inside AuthProvider.');
  }
  return context;
}

/** The signed-in, allowed user. Only call this below AuthGate, which renders nothing else until there is one. */
export function useCurrentUser(): CurrentUser {
  const { state } = useAuth();
  if (state.status !== 'authorized') {
    throw new Error('useCurrentUser must be used inside AuthGate.');
  }
  return state.user;
}
